
import { Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel, Input, InputProps, Stack } from '@chakra-ui/react';
import { TextGray } from 'components/Atomos/Texts/TextGray';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { Control, Controller, FieldError, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { isValidDate } from 'utils/date-utils';
import formatWithMask from 'utils/masks/format-with-masks';
import masks from 'utils/masks/masks';

export type DateFieldProps<T extends FieldValues> = InputProps & {
    control: Control<T>;
    name: Path<T>;
    rules?: Omit<RegisterOptions<T, Path<T>>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
    error?: FieldError;
    label?: string;
    helperText?: string;
    leftIcon?: React.ReactElement;
    rightIcon?: React.ReactElement;
}

const getValidDate = (maskedValue: string): Date | undefined => {
    const isMaskFilled = maskedValue.length === 10;
    if (isMaskFilled && maskedValue) {
        const textAsISO = maskedValue.split('/')?.reverse()?.join('-') ?? '';
        const textAsDate = new Date(`${textAsISO}T23:00`);
        const validDate = isValidDate(textAsISO);

        if (validDate) {
            return textAsDate;
        }
    }

    return undefined;
}

const DateField = <T extends FieldValues>({
    name,
    control,
    rules,
    label,
    error,
    helperText,
    leftIcon,
    ...rest
}: DateFieldProps<T>) => {    
    const isRequired = useMemo(() => !!rules?.required, [rules]);
    return (
        <FormControl isInvalid={!!error} isRequired={isRequired}>
            <Stack>
                {label && <FormLabel htmlFor={name} fontSize="sm" m={0}>
                    <Flex alignItems="center">
                        {leftIcon && (
                            <TextGray as="span" mr="1.5">
                                {leftIcon}
                            </TextGray>
                        )}
                        {label}
                    </Flex>
                </FormLabel>}
                <Controller
                    control={control}
                    name={name}
                    rules={rules}
                    render={({ field: { onChange, onBlur, value } }) => (
                        <Input
                            {...rest}                            
                            _placeholder={{ color: "gray.500" }}
                            InputLeftElement={leftIcon}
                            onChange={(e) => {
                                const text = e.currentTarget.value;
                                const { masked } = formatWithMask({
                                    text,
                                    mask: masks.DATE_DDMMYYYY,
                                });
                                const asDate = getValidDate(masked);
                                if (asDate) {
                                    onChange(asDate);
                                } else {
                                    onChange(masked);
                                }
                                e.currentTarget.value = masked;
                                rest?.onChange?.(e);
                            }}
                            onBlur={(e) => {
                                const isStringValue = typeof value === "string";
                                if (isStringValue) {
                                    onChange('');
                                }
                                onBlur();
                            }}
                            value={typeof value === "string" ? value : (value ? format(value, 'dd/MM/yyyy') : '')}
                        />
                    )}
                />
                {helperText && <FormHelperText pl={2}>
                    {helperText}
                </FormHelperText>}
                {error && <FormErrorMessage size="xs">
                    {error}
                </FormErrorMessage>}
            </Stack>
        </FormControl>
    )
}

export default DateField